/**
 * Which engine, and which Qwen region, the cost calculator opens on.
 *
 * The visitor's own last pick wins, then the engine (and region) their latest
 * session ran on, then Soniox (and Singapore). A pick is remembered in
 * `localStorage`, which is a convenience and nothing more: a private window,
 * blocked site data or a throwing accessor must leave the card working, only
 * without the memory.
 */
import { DEFAULT_QWEN_REGION, ENGINES, QWEN_REGIONS, type Engine, type QwenRegion } from './cost-model';
import type { LedgerGroup } from './ledger-labels';

export const ENGINE_STORAGE_KEY = 'billing.calculator.engine';

export const QWEN_REGION_STORAGE_KEY = 'billing.calculator.qwenRegion';

/**
 * How many usage groups the latest-session request asks for. A voice preview
 * is a usage group too, with the previewed engine's name on it, and a visitor
 * browsing voices leaves a run of them above their last session; one page
 * of twenty reaches past any such run.
 */
export const LATEST_SESSION_PAGE = 20;

/** The engine of the newest group that is a session — never a preview's. */
export function latestSessionEngine(groups: readonly Pick<LedgerGroup, 'kind' | 'engine'>[]): string | null {
  return groups.find((g) => g.kind === 'session')?.engine ?? null;
}

/**
 * The region of the newest group that is a session, from the same group
 * `latestSessionEngine` reads; null when that session names none (Soniox's, or a
 * group whose charges name several regions).
 */
export function latestSessionRegion(groups: readonly Pick<LedgerGroup, 'kind' | 'region'>[]): string | null {
  return groups.find((g) => g.kind === 'session')?.region ?? null;
}

/** The value as an engine this calculator prices, or null for anything else. */
export function asEngine(value: unknown): Engine | null {
  return ENGINES.find((engine) => engine === value) ?? null;
}

export function readStoredEngine(storage?: Pick<Storage, 'getItem'>): Engine | null {
  try {
    return asEngine((storage ?? window.localStorage).getItem(ENGINE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function storeEngine(engine: Engine, storage?: Pick<Storage, 'setItem'>): void {
  try {
    (storage ?? window.localStorage).setItem(ENGINE_STORAGE_KEY, engine);
  } catch {
    // Remembering the pick is optional; the card works without it.
  }
}

export function initialEngine(stored: Engine | null, latestSessionEngine: unknown): Engine {
  return stored ?? asEngine(latestSessionEngine) ?? 'soniox';
}

/** The value as a Qwen region this calculator prices, or null for anything else. */
export function asQwenRegion(value: unknown): QwenRegion | null {
  return QWEN_REGIONS.find((region) => region === value) ?? null;
}

/** The region the visitor picked on an earlier visit, or null without one (or without storage). */
export function readStoredRegion(storage?: Pick<Storage, 'getItem'>): QwenRegion | null {
  try {
    return asQwenRegion((storage ?? window.localStorage).getItem(QWEN_REGION_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Remember the visitor's region pick for the next visit; a no-op where storage is unavailable. */
export function storeRegion(region: QwenRegion, storage?: Pick<Storage, 'setItem'>): void {
  try {
    (storage ?? window.localStorage).setItem(QWEN_REGION_STORAGE_KEY, region);
  } catch {
    // Remembering the pick is optional; the card works without it.
  }
}

/** The region the calculator opens on: the stored pick, else the latest session's, else Singapore. */
export function initialRegion(stored: QwenRegion | null, latestSessionRegion: unknown): QwenRegion {
  return stored ?? asQwenRegion(latestSessionRegion) ?? DEFAULT_QWEN_REGION;
}
